import React from 'react'
import { Row, Text } from '.'
import { classNameBuilder } from '@/helpers/classNameBuilder'
import { Size } from '@/types/themeType'

type ButtonSize = Size | '2xl'

export type ButtonGroupProps = {
  value: string
  disabled?: boolean
  size?: ButtonSize
  onClick?: (value: string) => void
  className?: string
  options: { label: string; value: string }[]
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  size = 'xl',
  disabled = false,
  onClick,
  className,
  options,
  value
}) => {
  return (
    <Row className="items-center justify-between">
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => {
            onClick?.(option.value)
          }}
          className={classNameBuilder(
            'group/button relative overflow-hidden border font-bold uppercase outline-none',
            sizeClassMap[size],
            paddingClassMap[size],
            transitionClasses,
            disabled ? disabledClasses : '',
            !index ? 'rounded-l-full' : '',
            index + 1 === options.length ? 'rounded-r-full' : '',
            value && value === option.value
              ? 'border-electric-indigo-50 bg-electric-indigo-50 text-electric-indigo-500'
              : 'border-gray-50 text-gray-300 hover:bg-gray-50',
            className
          )}
        >
          <Text.Translated
            translationKey={option.label}
            size="xs"
            color="none"
            fontWeight="bold"
            className={
              value && value === option.value
                ? 'text-electric-indigo-500'
                : 'text-gray-300'
            }
          />
        </button>
      ))}
    </Row>
  )
}

export default ButtonGroup

const sizeClassMap: Record<ButtonSize, string> = {
  xs: 'min-h-[1.875rem] text-[10px]',
  sm: 'min-h-[2.25rem] text-[12px]',
  md: 'min-h-[2.5rem] text-sm',
  lg: 'min-h-[3rem] text-base',
  xl: 'min-h-[3.5rem] text-base',
  '2xl': 'min-h-[4rem] text-base'
}

const paddingClassMap: Record<ButtonSize, string> = {
  xs: 'px-2.5',
  sm: 'px-3',
  md: 'px-8',
  lg: 'px-8',
  xl: 'px-10',
  '2xl': 'px-10'
}

const transitionClasses = 'transition duration-300'

const disabledClasses =
  'disabled:text-opacity-50 disabled:shadow-none disabled:cursor-not-allowed'
